import { useEffect, useMemo, useRef, useState, FC } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import Stack from "@mui/material/Stack";
import type { SxProps, Theme } from "@mui/material";
import { isAxiosError } from "axios";
import { startImpersonation } from "../utils/impersonation";
import { axiosInstance } from "../utils/axios";
import { useDebounce } from "../hooks/useDebounce";

const styles: Record<string, SxProps<Theme>> = {
  dialog: {
    borderRadius: 3,
  },
  impersonateButton: {
    bgcolor: "warning.main",
    "&:hover": {
      bgcolor: "warning.dark",
    },
  },
};

interface ImpersonationDialogProps {
  open: boolean;
  onClose: () => void;
  initialTenantId?: number;
}

interface TenantOption {
  tenantId: number;
  name: string;
  domain?: string;
  active: boolean;
}

interface UserOption {
  userId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  active: boolean;
}

function escapeRegex(raw: string): string {
  return raw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function buildTenantDbQuery(search: string): Record<string, unknown> {
  const trimmed = search.trim();

  const base: Record<string, unknown> = {
    "status.active": true,
  };

  if (!trimmed) return base;

  const maybeId = Number.parseInt(trimmed, 10);
  const isIdQuery = Number.isFinite(maybeId) && String(maybeId) === trimmed;

  const pattern = escapeRegex(trimmed);

  return {
    ...base,
    $or: [
      ...(isIdQuery ? [{ _id: maybeId }] : []),
      { name: { $regex: pattern, $options: "i" } },
      { domain: { $regex: pattern, $options: "i" } },
    ],
  };
}

function formatTenantLabel(option: TenantOption): string {
  return option.domain
    ? `${option.name} (${option.tenantId}) — ${option.domain}`
    : `${option.name} (${option.tenantId})`;
}

function getServerErrorMessage(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data;

  if (!data) return null;
  if (typeof data === "string") return data;
  if (typeof data === "object" && data) {
    const maybeMessage = (data as { message?: unknown }).message;
    if (typeof maybeMessage === "string") return maybeMessage;
    const maybeError = (data as { error?: unknown }).error;
    if (typeof maybeError === "string") return maybeError;
  }

  return null;
}

function isValidEmail(email: string): boolean {
  const trimmed = email.trim();
  if (!trimmed) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(trimmed);
}

export const ImpersonationDialog: FC<ImpersonationDialogProps> = ({
  open,
  onClose,
  initialTenantId,
}) => {
  const [mode, setMode] = useState<"picker" | "email">("picker");

  const [tenantSearch, setTenantSearch] = useState("");
  const debouncedTenantSearch = useDebounce(tenantSearch, 400);
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(
    null
  );
  const [didUserClearPreselectedTenant, setDidUserClearPreselectedTenant] =
    useState(false);
  const hasAttemptedInitialTenantPrefillRef = useRef(false);

  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);

  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantHelperText = useMemo(() => {
    if (!selectedTenant) return "Select an organization to list its users";
    return "Now select the user email to impersonate";
  }, [selectedTenant]);
  const selectedTenantLabel = useMemo(
    () => (selectedTenant ? formatTenantLabel(selectedTenant) : ""),
    [selectedTenant]
  );

  const loadTenantById = async (
    tenantId: number
  ): Promise<TenantOption | null> => {
    const response = await axiosInstance.get("/argo-api/v1/tenant/paginated", {
      params: {
        limit: 1,
        skip: 0,
        dbQuery: JSON.stringify({
          _id: tenantId,
          "status.active": true,
        }),
      },
    });

    const rawTenants: unknown[] = Array.isArray(response.data)
      ? response.data
      : Array.isArray(response.data?.items)
        ? response.data.items
        : [];

    const item = rawTenants
      .map((t): TenantOption | null => {
        const tenantIdRaw = (t as { _id?: unknown })?._id;
        const parsedId =
          typeof tenantIdRaw === "number"
            ? tenantIdRaw
            : Number.parseInt(String(tenantIdRaw), 10);

        if (!Number.isFinite(parsedId)) return null;

        const name = (t as { name?: unknown })?.name;
        const domain = (t as { domain?: unknown })?.domain;
        const active = Boolean(
          (t as { status?: { active?: unknown } })?.status?.active
        );

        const tenant: TenantOption = {
          tenantId: parsedId,
          name: typeof name === "string" ? name : "",
          active,
          ...(typeof domain === "string" ? { domain } : {}),
        };

        return tenant;
      })
      .filter((t): t is TenantOption => t !== null)
      .find((t) => t.tenantId === tenantId);

    return item ?? null;
  };

  const loadTenants = async (search: string) => {
    setTenantsLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(
        "/argo-api/v1/tenant/paginated",
        {
          params: {
            limit: 20,
            skip: 0,
            dbQuery: JSON.stringify({
              ...buildTenantDbQuery(search),
            }),
          },
        }
      );

      const rawTenants: unknown[] = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];

      const items = rawTenants
        .map((t): TenantOption | null => {
          const tenantIdRaw = (t as { _id?: unknown })?._id;
          const tenantId =
            typeof tenantIdRaw === "number"
              ? tenantIdRaw
              : Number.parseInt(String(tenantIdRaw), 10);

          if (!Number.isFinite(tenantId)) return null;

          const name = (t as { name?: unknown })?.name;
          const domain = (t as { domain?: unknown })?.domain;
          const active = Boolean(
            (t as { status?: { active?: unknown } })?.status?.active
          );

          const tenant: TenantOption = {
            tenantId,
            name: typeof name === "string" ? name : "",
            active,
            ...(typeof domain === "string" ? { domain } : {}),
          };

          return tenant;
        })
        .filter((t): t is TenantOption => t !== null);

      setTenantOptions(() => {
        if (!selectedTenant) return items;
        if (items.some((t) => t.tenantId === selectedTenant.tenantId)) {
          return items;
        }
        return [selectedTenant, ...items];
      });
    } catch (err) {
      const serverMessage = getServerErrorMessage(err);
      setError(
        serverMessage ||
          "Failed to load organizations. You can use email instead."
      );
      setMode("email");
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadUsersForTenant = async (tenantId: number) => {
    setUsersLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(
        `/argo-api/v1/tenant/${tenantId}/users`
      );

      const rawUsers: unknown[] = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];

      const items = rawUsers
        .filter((u) => Boolean((u as { active?: unknown })?.active))
        .map((u): UserOption | null => {
          const userIdRaw = (u as { _id?: unknown })?._id;
          const userId =
            typeof userIdRaw === "string"
              ? userIdRaw
              : typeof userIdRaw === "number"
                ? String(userIdRaw)
                : null;
          const email = (u as { email?: unknown })?.email;

          if (!userId) return null;
          if (typeof email !== "string" || !email) return null;

          const firstName = (u as { first_name?: unknown })?.first_name;
          const lastName = (u as { last_name?: unknown })?.last_name;

          const user: UserOption = {
            userId,
            email,
            active: true,
            ...(typeof firstName === "string" ? { firstName } : {}),
            ...(typeof lastName === "string" ? { lastName } : {}),
          };

          return user;
        })
        .filter((u): u is UserOption => u !== null);

      setUserOptions(items);
    } catch (err) {
      const serverMessage = getServerErrorMessage(err);
      setError(
        serverMessage || "Failed to load users. You can use email instead."
      );
      setMode("email");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setDidUserClearPreselectedTenant(false);
      hasAttemptedInitialTenantPrefillRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (mode !== "picker") return;
    if (!initialTenantId) return;
    if (selectedTenant) return;
    if (didUserClearPreselectedTenant) return;
    if (hasAttemptedInitialTenantPrefillRef.current) return;

    let cancelled = false;

    hasAttemptedInitialTenantPrefillRef.current = true;
    setTenantsLoading(true);
    setError(null);

    void (async () => {
      try {
        const tenant = await loadTenantById(initialTenantId);
        if (cancelled) return;

        if (!tenant) {
          setError(
            `Organization ${initialTenantId} not found. You can use email instead.`
          );
          setMode("email");
          return;
        }

        setSelectedTenant(tenant);
        setTenantOptions([tenant]);
        setTenantSearch(formatTenantLabel(tenant));
      } catch (err) {
        if (cancelled) return;
        const serverMessage = getServerErrorMessage(err);
        setError(
          serverMessage ||
            "Failed to load organization. You can use email instead."
        );
        setMode("email");
      } finally {
        if (!cancelled) setTenantsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    mode,
    initialTenantId,
    selectedTenant,
    didUserClearPreselectedTenant,
  ]);

  useEffect(() => {
    if (!open) return;
    if (mode !== "picker") return;

    const trimmedSearch = debouncedTenantSearch.trim();

    // Ignore the synthetic search generated after selecting an option.
    if (selectedTenant && trimmedSearch === selectedTenantLabel) {
      return;
    }

    void loadTenants(trimmedSearch);
  }, [open, mode, debouncedTenantSearch, selectedTenant, selectedTenantLabel]);

  useEffect(() => {
    if (!open) return;
    if (mode !== "picker") return;

    if (!selectedTenant?.tenantId) {
      setUserOptions([]);
      setSelectedUser(null);
      return;
    }

    void loadUsersForTenant(selectedTenant.tenantId);
  }, [open, mode, selectedTenant?.tenantId]);

  useEffect(() => {
    if (mode !== "email") return;
    setTenantSearch("");
    setTenantOptions([]);
    setSelectedTenant(null);
    setUserOptions([]);
    setSelectedUser(null);
  }, [mode]);

  const handleImpersonate = async () => {
    const emailToImpersonate =
      mode === "picker" ? selectedUser?.email : email.trim();

    if (!emailToImpersonate) {
      setError(
        mode === "picker"
          ? "Please select a user email"
          : "Please enter an email address"
      );
      return;
    }

    if (!isValidEmail(emailToImpersonate)) {
      setError("Please enter a valid email address");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      await startImpersonation(emailToImpersonate.trim());
      window.location.reload();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to impersonate user. Please check the email and try again.";
      setError(errorMessage);
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setEmail("");
    setMode("picker");
    setTenantSearch("");
    setTenantOptions([]);
    setSelectedTenant(null);
    setDidUserClearPreselectedTenant(false);
    setUserOptions([]);
    setSelectedUser(null);
    setError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !submitting) {
      handleImpersonate();
    }
  };

  return (
    <Dialog
      id="impersonation-dialog"
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: { sx: styles.dialog },
      }}
    >
      <DialogTitle id="impersonation-dialog-title">
        <Typography variant="h6" fontWeight={600}>
          Impersonate User
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Select an organization and user email to impersonate. This is for
          debugging and support purposes only.
        </Typography>
      </DialogTitle>

      <DialogContent id="impersonation-dialog-content">
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert
              id="impersonation-dialog-error-alert"
              severity="error"
              sx={{ mb: 2 }}
              action={
                mode === "picker" ? (
                  <Button
                    id="impersonation-dialog-use-email-button"
                    color="inherit"
                    size="small"
                    onClick={() => setMode("email")}
                    disabled={submitting}
                  >
                    Use email
                  </Button>
                ) : (
                  <Button
                    id="impersonation-dialog-use-picker-button"
                    color="inherit"
                    size="small"
                    onClick={() => setMode("picker")}
                    disabled={submitting}
                  >
                    Use picker
                  </Button>
                )
              }
            >
              {error}
            </Alert>
          )}

          {mode === "picker" ? (
            <Stack spacing={2}>
              <Autocomplete<TenantOption, false, false, false>
                id="impersonation-dialog-tenant-id"
                fullWidth
                options={tenantOptions}
                loading={tenantsLoading}
                value={selectedTenant}
                onChange={(_, value, reason) => {
                  if (reason === "clear") {
                    setDidUserClearPreselectedTenant(true);
                  } else if (value) {
                    setDidUserClearPreselectedTenant(false);
                  }

                  setSelectedTenant(value);
                  setTenantSearch(value ? formatTenantLabel(value) : "");
                  setUserOptions([]);
                  setSelectedUser(null);
                }}
                inputValue={tenantSearch}
                onInputChange={(_, value, reason) => {
                  if (reason === "input" || reason === "clear") {
                    setTenantSearch(value);
                  }
                }}
                getOptionLabel={(option) => formatTenantLabel(option)}
                isOptionEqualToValue={(option, value) =>
                  option.tenantId === value.tenantId
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="impersonation-dialog-tenant-input"
                    autoFocus
                    label="Organization / Tenant"
                    placeholder="Search organizations..."
                    helperText={tenantHelperText}
                    disabled={submitting}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {tenantsLoading ? (
                            <CircularProgress
                              color="inherit"
                              size={16}
                              sx={{ mr: 1 }}
                            />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <Autocomplete<UserOption, false, false, false>
                id="impersonation-dialog-user-email"
                fullWidth
                options={userOptions}
                loading={usersLoading}
                value={selectedUser}
                onChange={(_, value) => setSelectedUser(value)}
                getOptionLabel={(option) => option.email}
                isOptionEqualToValue={(option, value) =>
                  option.userId === value.userId
                }
                disabled={!selectedTenant || submitting}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="impersonation-dialog-user-input"
                    label="User email"
                    placeholder={
                      selectedTenant
                        ? "Select a user..."
                        : "Select an organization first"
                    }
                  />
                )}
              />
            </Stack>
          ) : (
            <TextField
              id="impersonation-dialog-email-input"
              autoFocus
              fullWidth
              label="Target User Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={submitting}
              placeholder="user@example.com"
              helperText="The session will be switched to this user's account"
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions id="impersonation-dialog-actions" sx={{ px: 3, pb: 2 }}>
        <Button
          id="impersonation-dialog-cancel-button"
          onClick={handleClose}
          disabled={submitting}
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          id="impersonation-dialog-impersonate-button"
          onClick={handleImpersonate}
          disabled={
            submitting ||
            (mode === "picker" ? !selectedUser?.email : !email.trim())
          }
          variant="contained"
          sx={styles.impersonateButton}
        >
          {submitting ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            "Impersonate"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
